import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {ChemistryPreferences, DEFAULT_MIN_SIMILARITY_CUTOFF} from 'chemistry/ChemistryConstants';
import {getDefaultChemSearchOperator, ToggleHighlightParams} from 'chemistry/ChemSearchUtils';
import {
  ChemicalStructureDrawer,
  ChemicalStructureDrawerMode,
  ChemicalStructureDrawerValue,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import preferences from 'Preferences';
import {
  ChemicalStructureSearchOperator,
  ChemicalStructureSearchParams,
  Operator,
  SearchParams,
} from 'search/SearchConstants';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';

import ChemSearchModalOptions from './ChemSearchModalOptions';

interface ChemSearchModalProps {
  initialMol: string | null;
  onRequestSubmit: (chemSearchParams: ChemicalStructureSearchParams) => void;
  onRequestClose: () => void;
  toggleHighlightParams: ToggleHighlightParams;
  searchParams: SearchParams;
}

export default function ChemSearchModal({
  initialMol,
  onRequestSubmit,
  onRequestClose,
  toggleHighlightParams,
  searchParams,
}: ChemSearchModalProps): JSX.Element {
  const [mol, setMol] = useState<ChemicalStructureDrawerValue>(initialMol);
  const [highlightChecked, setHighlightChecked] = useState<boolean>(
    toggleHighlightParams.shouldHighlightSubstructure
  );
  const [chemSearchOperator, setChemSearchOperator] = useState<ChemicalStructureSearchOperator>(
    getDefaultChemSearchOperator(searchParams)
  );
  const [minSimilarity, setMinSimilarity] = useState<number>(
    preferences.get(ChemistryPreferences.MIN_SIMILARITY) ?? DEFAULT_MIN_SIMILARITY_CUTOFF
  );

  const savePreferencesAndSubmitSearchParams = (): void => {
    preferences.set(ChemistryPreferences.CHEM_SEARCH_TYPE, chemSearchOperator);
    if (toggleHighlightParams.showToggleHighlightOption) {
      toggleHighlightParams.setShouldHighlightSubstructure(highlightChecked);
    }
    if (chemSearchOperator === Operator.SIMILARITY) {
      preferences.set(ChemistryPreferences.MIN_SIMILARITY, minSimilarity);
      onRequestSubmit({
        chemicalStructureQuery: mol,
        chemSearchOperator,
        minSimilarity,
      });
    } else {
      onRequestSubmit({
        chemicalStructureQuery: mol,
        chemSearchOperator,
      });
    }
  };

  return (
    <ExpandableModal
      enableRedesign={true}
      title="Chemical structure search"
      buttons={[
        {
          text: 'Search',
          submit: true,
          dataTestAttrs: {component: 'ChemSearchModal', element: 'submit'},
        },
      ]}
      onRequestClose={onRequestClose}
      onRequestSubmit={savePreferencesAndSubmitSearchParams}
      expandHeight={true}
      scrollable={true}
    >
      <div className={css(styles.chemicalDrawer)}>
        <ChemicalStructureDrawer
          onMoleculeChange={setMol}
          initialValue={mol}
          mode={ChemicalStructureDrawerMode.STANDARD}
        />
      </div>
      <ChemSearchModalOptions
        highlightChecked={highlightChecked}
        setHighlightChecked={setHighlightChecked}
        chemSearchOperator={chemSearchOperator}
        setChemSearchOperator={setChemSearchOperator}
        minSimilarity={minSimilarity}
        setMinSimilarity={setMinSimilarity}
        toggleHighlightParams={toggleHighlightParams}
        className={css(styles.searchOptions)}
      />
    </ExpandableModal>
  );
}

const styles = StyleSheet.create({
  chemicalDrawer: {
    flex: 1,
    display: 'flex',
    minHeight: 500,
    paddingBottom: 12,
  },
  searchOptions: {
    paddingTop: 12,
    borderTop: `1px solid ${GRAY_LIGHT}`,
  },
});
